"use client";

import { JSX, useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/lib/utils";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { CascadeIn } from "@/components/motion/cascade-in";
import { SegmentTabs } from "@/components/motion/segment-tabs";
import { useViewerLanguage } from "@/lib/i18n";
import { emitModuleInteraction } from "@/lib/module-interaction";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import type {
  KeywordGroup,
  ModuleKeywordsExplainedData,
} from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";
import {
  buildKeywordBuckets,
  KeywordChip,
  keywordGroupColors,
  keywordWeightLabels,
  normalizeKeywords,
  resolveKeywordColor,
} from "./keyword-shared";
import { accentChartColors } from "./module-accent";

const ALL = "__all__";

function Arrow({ dir }: { dir: "left" | "right" }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden
      style={{ transform: dir === "left" ? "scaleX(-1)" : undefined }}
    >
      <path
        d="M3 7h8M8 4l3 3-3 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ModuleKeywordsExplained({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleKeywordsExplainedData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const items = data.items;
  const groups = data.groups;
  const allLabel = data.allLabel ?? t("All");
  const otherLabel = t("Other");
  const weightLabels = useMemo(() => keywordWeightLabels(t), [t]);

  const list = useMemo(() => normalizeKeywords(items), [items]);
  const groupColors = useMemo(
    () => keywordGroupColors(groups, accentChartColors(grayscale, accent)),
    [groups, grayscale, accent],
  );
  const groupById = useMemo(
    () =>
      Object.fromEntries(groups.map((g) => [g.id, g])) as Record<
        string,
        KeywordGroup
      >,
    [groups],
  );

  const [bucket, setBucket] = useState<string>(ALL);
  const [selected, setSelected] = useState<string | null>(list[0]?.id ?? null);
  const [hovered, setHovered] = useState<string | null>(null);
  const hasHover = useHoverCapable();

  const buckets = useMemo(
    () => buildKeywordBuckets(list, "group", groups, otherLabel, weightLabels),
    [list, groups, otherLabel, weightLabels],
  );

  const shown =
    bucket === ALL
      ? list
      : (buckets.find((b) => b.id === bucket)?.items ?? list);

  const activeId =
    selected === null
      ? null
      : shown.some((k) => k.id === selected)
        ? selected
        : (shown[0]?.id ?? null);
  const index = shown.findIndex((k) => k.id === activeId);
  const kw = index >= 0 ? shown[index] : null;

  const step = (d: number) => {
    if (!shown.length) return;
    setSelected(shown[(index + d + shown.length) % shown.length].id);
  };

  const meta = kw
    ? [kw.group && groupById[kw.group]?.label, weightLabels[kw.weight]]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <div
      className={cn("w-full", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      {buckets.length > 1 && (
        <div className="mb-4 w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <SegmentTabs
            value={bucket}
            onValueChange={(v) => v !== null && setBucket(v)}
            options={[
              { value: ALL, label: allLabel, count: list.length },
              ...buckets.map((b) => ({
                value: b.id,
                label: stripInlineMarkdown(b.label),
                count: b.items.length,
              })),
            ]}
          />
        </div>
      )}

      <div className="w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <CascadeIn
          keySignature={bucket}
          className="flex flex-wrap gap-1.5"
          itemClassName="inline-flex"
        >
          {shown.map((k) => {
            const isSelected = k.id === activeId;
            const isHovered = hovered === k.id;
            const isDimmed =
              (activeId !== null && !isSelected && hovered === null) ||
              (hovered !== null && !isHovered);
            return (
              <KeywordChip
                key={k.id}
                text={stripInlineMarkdown(k.text)}
                weight={k.weight}
                variant={k.variant}
                color={resolveKeywordColor(k, groupColors)}
                ariaPressed={isSelected}
                state={isHovered || isSelected ? "hover" : null}
                dim={isDimmed}
                onMouseEnter={hasHover ? () => setHovered(k.id) : undefined}
                onMouseLeave={hasHover ? () => setHovered(null) : undefined}
                onClick={(e) => {
                  setSelected(k.id);
                  emitModuleInteraction(e.currentTarget, "select", {
                    kind: "card",
                    id: k.id,
                  });
                }}
              />
            );
          })}
        </CascadeIn>
      </div>

      <motion.div
        layout
        transition={{ layout: { duration: 0.32, ease: [0.4, 0, 0.2, 1] } }}
        className={cn(kw && "mt-5")}
      >
        <AnimatePresence mode="wait" initial={false}>
          {kw && (
            <motion.div
              key={kw.id}
              initial={{ opacity: 0, y: 6, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -6, filter: "blur(6px)" }}
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              className="overflow-hidden rounded-xl border border-[var(--border-strong)]"
              aria-live="polite"
            >
              <div className="handyout-pattern-diag p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div
                      className={`text-2xl font-medium tracking-tight ${MODULE_LABEL_WRAP.heading}`}
                    >
                      {stripInlineMarkdown(kw.text)}
                    </div>
                    {meta && (
                      <div className="mt-1 text-sm text-[var(--muted)]">
                        {meta}
                      </div>
                    )}
                  </div>
                  {shown.length > 1 && (
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          step(-1);
                          emitModuleInteraction(e.currentTarget, "click", {
                            kind: "control",
                            id: "prev",
                          });
                        }}
                        aria-label={t("Previous keyword")}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-[var(--border-strong)] text-[var(--muted)] transition-colors hover:bg-[var(--track)] hover:text-[var(--fg)]"
                      >
                        <Arrow dir="left" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          step(1);
                          emitModuleInteraction(e.currentTarget, "click", {
                            kind: "control",
                            id: "next",
                          });
                        }}
                        aria-label={t("Next keyword")}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-[var(--border-strong)] text-[var(--muted)] transition-colors hover:bg-[var(--track)] hover:text-[var(--fg)]"
                      >
                        <Arrow dir="right" />
                      </button>
                    </div>
                  )}
                </div>
                {kw.description && (
                  <p className="mt-4 max-w-xl text-[15px] leading-relaxed">
                    {stripInlineMarkdown(kw.description)}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-dashed border-[var(--border-dashed)] bg-[var(--card)] px-5 py-3">
                <span className="text-xs tabular-nums text-[var(--subtle)]">
                  {index + 1} of {shown.length}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
